import React, { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Checkbox } from '@swyftx/aviary/atoms/Checkbox';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, BodyLink } from '@swyftx/aviary/atoms/Typography';
import StepModal from '@swyftx/aviary/molecules/StepModal';

import { Links } from '@shared/constants';

import { useAvo } from '@hooks/Avo/useAvo';

import { ModalName } from 'src/context/Avo/generated-avo';

import { TransferModalStep } from '../TransferModal.types';

type Props = {
  onBack: (stepsBack?: number) => void;
  onContinue: () => void;
};

const WithdrawalAcknowledgementStep: React.FC<PropsWithChildren<Props>> = ({ onBack, onContinue }) => {
  const [accepted, setAccepted] = useState<boolean>(false);
  const { t } = useTranslation('wallet');
  const { pathname } = useLocation();
  const avo = useAvo();

  avo.modalOpened({ modalName: ModalName.WITHDRAWAL_ADDRESS_ACKNOWLEDGEMENT, screen: pathname });

  return (
    <StepModal.Step
      hideActions
      hideBack
      title={t('cryptoWithdrawalAcknowledgement.title')}
      key={TransferModalStep.WithdrawalAcknowledgement}
    >
      <FlexLayout direction='column' spacing={24} className='px-24 pb-24'>
        <FlexLayout direction='column' spacing={8}>
          <Body size='medium' color='secondary'>
            {t('cryptoWithdrawalAcknowledgement.subTitle')}
          </Body>
          <ul className='list-disc pl-8 text-10 text-color-text-secondary'>
            <li>
              <Body size='medium' color='secondary'>
                {t('cryptoWithdrawalAcknowledgement.gambling')}
              </Body>
            </li>
            <li>
              <Body size='medium' color='secondary'>
                {t('cryptoWithdrawalAcknowledgement.pharma')}
              </Body>
            </li>
            <li>
              <Body size='medium' color='secondary'>
                {t('cryptoWithdrawalAcknowledgement.terms')}{' '}
                <BodyLink
                  size='medium'
                  color='accent'
                  target='_blank'
                  href={Links.legal.tos}
                  className='cursor-pointer hover:underline'
                >
                  {t('cryptoWithdrawalAcknowledgement.termsLink')}
                </BodyLink>
              </Body>
            </li>
          </ul>
        </FlexLayout>

        <FlexLayout spacing={8}>
          <Checkbox checked={accepted} onCheckedChange={(checked) => setAccepted(!!checked)} />
          <Body size='medium' className='cursor-pointer' onClick={() => setAccepted((prev) => !prev)}>
            {t('cryptoWithdrawalAcknowledgement.acceptTerms')}
          </Body>
        </FlexLayout>

        <FlexLayout direction='column' spacing={16}>
          <Button variant='filled' disabled={!accepted} onClick={onContinue} size='lg'>
            Continue
          </Button>
          <Button variant='outlined' onClick={() => onBack()} size='lg'>
            Go back
          </Button>
        </FlexLayout>
      </FlexLayout>
    </StepModal.Step>
  );
};

export { WithdrawalAcknowledgementStep };
